import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  DEFAULT_SECTION,
  EXTRANET_SECTIONS,
  ExtranetRole,
  extranetPath,
  parseSection,
} from "../lib/routes";

interface ExtranetSectionState<S extends string> {
  /** Section currently displayed, always a valid one. */
  section: S;
  /** Second path segment (tab inside a section), raw. */
  sub: string | undefined;
  /** Current query string params of the extranet URL. */
  params: URLSearchParams;
  /** Navigates to another section of the same extranet. */
  goToSection: (
    section: S,
    options?: {
      sub?: string;
      query?: Record<string, string | undefined>;
      replace?: boolean;
      /** Router state to carry over (e.g. the settings tab to open). */
      state?: unknown;
    },
  ) => void;
  /** Builds a link target — used by the sidebar so entries are real links. */
  sectionPath: (
    section: S,
    options?: { sub?: string; query?: Record<string, string | undefined> },
  ) => string;
}

/**
 * Reads the active extranet section from the URL (`/extranet/:role/:section`)
 * instead of component state, so the browser's back/forward buttons, a reload
 * and a pasted link all land on the right page.
 *
 * Unknown or missing sections are rewritten to the role's default section, and
 * the legacy `?section=` form is redirected to its path equivalent.
 */
export function useExtranetSection<R extends ExtranetRole>(
  role: R,
): ExtranetSectionState<(typeof EXTRANET_SECTIONS)[R][number]> {
  type Section = (typeof EXTRANET_SECTIONS)[R][number];

  const navigate = useNavigate();
  const routeParams = useParams();
  const [searchParams] = useSearchParams();

  const sections = EXTRANET_SECTIONS[role];
  const fallback = DEFAULT_SECTION[role] as Section;

  const fromPath = parseSection(sections, routeParams.section) as Section | null;
  const fromQuery = parseSection(
    sections,
    searchParams.get("section") ?? undefined,
  ) as Section | null;
  const section = fromPath ?? fromQuery ?? fallback;
  const sub = routeParams.sub;

  // Canonicalise the URL: no section in the path (or an unknown one) means the
  // address bar doesn't describe the page — rewrite it without a history entry.
  useEffect(() => {
    if (fromPath) return;
    const query = new URLSearchParams(searchParams);
    query.delete("section");
    navigate(
      `${extranetPath(role, section)}${query.toString() ? `?${query}` : ""}`,
      { replace: true },
    );
  }, [fromPath, navigate, role, searchParams, section]);

  const sectionPath = useCallback(
    (
      target: Section,
      options?: { sub?: string; query?: Record<string, string | undefined> },
    ) => extranetPath(role, target, options?.sub, options?.query),
    [role],
  );

  const goToSection = useCallback(
    (
      target: Section,
      options?: {
        sub?: string;
        query?: Record<string, string | undefined>;
        replace?: boolean;
        state?: unknown;
      },
    ) =>
      navigate(sectionPath(target, options), {
        replace: options?.replace,
        state: options?.state,
      }),
    [navigate, sectionPath],
  );

  const params = useMemo(
    () => new URLSearchParams(searchParams),
    [searchParams],
  );

  return { section, sub, params, goToSection, sectionPath };
}
